import { gql, useApolloClient, useMutation } from '@apollo/client';
import { RegisterWaste, RegisterWasteVariables } from './__queries__';
import TimeManager from '~Components/TimeManager';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';
import { itemInBong } from '~Scenes/POS/Queries/__queries__';
import * as actions from '~actions';
import { v4 as uuid } from 'uuid';
import { useStorage } from '~Components/Storage';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';

const REGISTER_WASTE = gql`
  mutation RegisterWaste($input: RegisterWasteInput!) {
    registerWaste(input: $input) {
      id
      success
      error
    }
  }
`;

interface WasteEventInput {
  items: {
    itemId: string;
    fraction?: number;
    title?: string;
    cost?: number;
  }[];
  reason: string;
  comment?: string;
}

/** Hook handling Waste - Meaning products that have been wasted, such as broken, expired or similar */
export const useWaste = () => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const logger = useLogger('waste');
  const client = useApolloClient();
  const [storage] = useStorage();

  const staffId = useSelector((state) => state.staff?.id);
  const venueId = useSelector((state) => state.venueId) as string;

  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);

  const [registerWaste] = useMutation<RegisterWaste, RegisterWasteVariables>(REGISTER_WASTE);

  const generateEventMeta = () => ({
    createdAt: TimeManager.now().toISOString(),
    staffId,
    venueId,
    posDeviceId: storage.taxComplianceDeviceName || 'Unknown',
  });

  /**
   * Registers waste items
   * We need the title to handle manual named items but also volume based items to log correctly how many cl/kg etc were wasted
   * @param items - Array of waste items with itemId, fraction, title, and optional message
   * @param reason - The reason for the waste (e.g., "Stolen"). Reasons are better for reporting visualizations.
   * @param comment - Optional additional descriptive comment about the waste event (e.g., "Customer ran off without paying"). Comments are better for giving context
   */
  const register = async (
    items: { itemId: string; fraction: number; title?: string; message?: string | null }[],
    reason: string,
    comment: string,
  ) => {
    logger.info('Registering waste', { items, reason, comment });

    let lastError: Error | null = null;
    const maxRetries = 4;

    const wasteItemsInput: WasteEventInput['items'] = items.map((item) => {
      const itemFragment = client.readFragment<itemInBong>({
        id: `Item${item.itemId}`,
        fragment: ITEM_FRAGMENT,
      });

      if (!itemFragment) {
        return { itemId: item.itemId, fraction: item.fraction };
      }

      /**
       * To get how many units that has been selected we need to reverse the title that is created here
       * src/Scenes/POS/Components/GridButtons/ItemButton/index.tsx -> addItemWithVolume()
       */
      if (itemFragment.volumeSuffix && item.title) {
        // Find where the volume suffix (e.g., "cl") appears in the title
        const suffixIndex = item.title.indexOf(itemFragment.volumeSuffix);

        // Extract everything before the suffix (e.g., "4" from "4cl Grey Goose Vodka")
        const volumeString = item.title.substring(0, suffixIndex);

        // Convert to number
        const volumeCount = parseFloat(volumeString);

        return {
          itemId: item.itemId,
          title: item.title,
          cost: Math.round(itemFragment.costBasis * volumeCount),
        };
      }

      const invItem = inventoryUniverse.items.find((i) => i.itemId === item.itemId);

      if (invItem && typeof invItem.count === 'number') {
        inventoryApi.reduceStock(invItem.itemId, item.fraction, reason);
      }

      return {
        itemId: item.itemId,
        title: itemFragment.manualName ? item.message || item.title : item.title,
        fraction: item.fraction,
        /** Enable cost to get sent in as 0 */
        cost:
          itemFragment.costBasis != null
            ? Math.round(itemFragment.costBasis * item.fraction)
            : undefined,
      };
    });

    const wasteInput: WasteEventInput = {
      items: wasteItemsInput,
      reason,
    };

    if (comment !== '') {
      wasteInput.comment = comment;
    }

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        await registerWaste({
          variables: {
            input: {
              ...generateEventMeta(),
              ...wasteInput,
            },
          },
        });

        dispatch(actions.addToast(i18n('Waste.WasteRegisteredSuccessfully'), true));

        if (attempt > 0) {
          logger.info('Waste registration succeeded after retry', {
            attempt,
            wasteItemsInput,
            reason,
          });
        } else {
          logger.info('Waste registration succeeded', { wasteItemsInput, reason });
        }

        return;
      } catch (error) {
        lastError = error as Error;

        if (attempt < maxRetries) {
          logger.warn('Waste registration failed, retrying', {
            attempt: attempt + 1,
            maxRetries,
            errorMessage: lastError?.message,
            wasteItemsInput,
            reason,
          });

          await new Promise((resolve) => setTimeout(resolve, 4000));
        }
      }
    }

    logger.error('Waste registration failed after all retries', {
      maxRetries,
      errorMessage: lastError?.message,
      wasteItemsInput,
      reason,
    });

    dispatch(actions.addToast('Waste.WasteRegisteredFailed', false, uuid(), true));

    throw lastError;
  };

  return { register };
};
